import { c as _c } from "react/compiler-runtime";
import { useApolloClient, useQuery } from '@apollo/client/react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { Segmented } from '@src/components/sco/Advisors/shared/Segmented';
import { formatWhen } from '@src/components/sco/Advisors/utils/sessionFormat';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import { ADVISOR_SESSION_CHAT } from '@src/gql/queries/AdvisorSessionChat';
import { ADVISORS_ATTENTION } from '@src/gql/queries/AdvisorsAttention';
import { ScoAdvisorSession } from '@src/graphql-types';
import { ReactNode, useEffect, useState } from 'react';

/** Give the thread read (which clears its unread count server-side) time to land before refreshing badges. */
const BADGE_REFRESH_DELAY_MS = 1500;
export const SESSION_CHAT_TAB = 'This session';
export const DIRECT_CHAT_TAB = 'Messages';
export type SessionChatTab = typeof SESSION_CHAT_TAB | typeof DIRECT_CHAT_TAB;

/** Session thread id, preferring the resolved thread. Null for legacy rows whose hold never authorised. */
export const sessionChatThreadId = (session?: Pick<ScoAdvisorSession, 'chatThreadId' | 'chatThread'> | null) => session?.chatThread?.threadId || session?.chatThreadId || null;

/** The existing DM between the viewer and the other party (never created here). */
export const counterpartDirectThreadId = (session?: Pick<ScoAdvisorSession, 'currentUserRole' | 'expertUser' | 'seekerUser'> | null) => {
  if (!session) {
    return null;
  }
  const counterpart = session.currentUserRole === 'EXPERT' ? session.seekerUser : session.expertUser;
  return counterpart?.directChatThreadWithCurrentUser?.threadId || null;
};
export type SessionChatPanelViewProps = {
  sessionThreadId: string | null;
  directThreadId: string | null;
  title?: string;
  reason?: string | null;
  scheduledAt?: string | null;
  loading?: boolean;
  /** Override for stories; defaults to the shared `ChatThread`. */
  renderThread?: (threadId: string, tab: SessionChatTab) => ReactNode;
  /** Called when a thread is opened (and so marked read). */
  onThreadShown?: (threadId: string, tab: SessionChatTab) => void;
};
const defaultRenderThread = (threadId: string, tab: SessionChatTab) => <ChatThread key={threadId} hideHeader
// Session chat is scoped to the two parties by the session; DMs keep normal chat permissions.
bypassContentPermissions={tab === SESSION_CHAT_TAB} enableFileAttachments={false} threadId={threadId} />;

/**
 * L19: session chat with a "This session / Messages" toggle. Messages is the
 * pair's existing direct thread; the toggle is hidden when there is none.
 * Session chat stays out of Inbox and the Chat icon — it only lives here.
 */
export const SessionChatPanelView = ({
  sessionThreadId,
  directThreadId,
  title = 'Session chat',
  reason,
  scheduledAt,
  loading = false,
  renderThread = defaultRenderThread,
  onThreadShown
}: SessionChatPanelViewProps) => {
  const [tab, setTab] = useState<SessionChatTab>(SESSION_CHAT_TAB);
  const showToggle = !!directThreadId;
  const activeTab: SessionChatTab = showToggle ? tab : SESSION_CHAT_TAB;
  const threadId = activeTab === DIRECT_CHAT_TAB ? directThreadId : sessionThreadId;
  useEffect(() => {
    if (threadId) {
      onThreadShown?.(threadId, activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, activeTab]);
  return <VStack align={'stretch'} gap={0} height={'full'} minHeight={0} width={'full'}>
      <VStack align={'stretch'} borderBottomWidth={'1px'} flexShrink={0} gap={2} paddingX={3} paddingY={3}>
        <Box>
          <Text fontSize={'sm'} fontWeight={'semibold'}>
            {title}
          </Text>
          {reason ? <Text color={'colorPalette.fg/60'} fontFamily={'longform'} fontSize={'xs'} lineClamp={2}>
              {reason}
            </Text> : null}
          {scheduledAt ? <Text color={'colorPalette.fg/50'} fontSize={'xs'}>
              {formatWhen(scheduledAt)}
            </Text> : null}
        </Box>
        {showToggle ? <Segmented options={[SESSION_CHAT_TAB, DIRECT_CHAT_TAB]} size={'xs'} value={activeTab} onChange={value => setTab(value === DIRECT_CHAT_TAB ? DIRECT_CHAT_TAB : SESSION_CHAT_TAB)} /> : null}
      </VStack>
      <Box flex={1} minHeight={0}>
        {threadId ? renderThread(threadId, activeTab) : <Text color={'colorPalette.fg/50'} fontSize={'sm'} padding={3}>
            {loading ? 'Loading chat…' : 'Chat is not available for this session.'}
          </Text>}
      </Box>
    </VStack>;
};

/** Data-bound panel used by session detail and the room sidebar. */
export const SessionChatPanel = t0 => {
  const $ = _c(20);
  const {
    advisorSessionId,
    reason,
    scheduledAt
  } = t0;
  let t1;
  if ($[0] !== advisorSessionId) {
    t1 = {
      advisorSessionId
    };
    $[0] = advisorSessionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !advisorSessionId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      variables: t1,
      skip: t2,
      errorPolicy: "all"
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const query = useQuery(ADVISOR_SESSION_CHAT, t3);
  const session = query.data?.advisorSession;
  const client = useApolloClient();
  const [shownAt, setShownAt] = useState(0);
  let t4;
  let t5;
  if ($[5] !== client || $[6] !== shownAt) {
    t4 = () => {
      if (!shownAt) {
        return;
      }
      const timer = window.setTimeout(() => {
        client.refetchQueries({
          include: [ADVISORS_ATTENTION]
        }).catch(_temp);
      }, BADGE_REFRESH_DELAY_MS);
      return () => window.clearTimeout(timer);
    };
    t5 = [client, shownAt];
    $[5] = client;
    $[6] = shownAt;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== session) {
    t6 = counterpartDirectThreadId(session);
    $[9] = session;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = query.loading && !session;
  let t8;
  if ($[11] !== session) {
    t8 = sessionChatThreadId(session);
    $[11] = session;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setShownAt(Date.now());
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== reason || $[15] !== scheduledAt || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t10 = <SessionChatPanelView directThreadId={t6} loading={t7} reason={reason} scheduledAt={scheduledAt} sessionThreadId={t8} onThreadShown={t9} />;
    $[14] = reason;
    $[15] = scheduledAt;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
function _temp() {}